import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { useLanguage } from '@/contexts/AppContext';
import { isMenuItemSoldOut, isMenuItemAvailableNow, hasDiscount, type MenuItem } from '@/data';
import { isMenuItemDisabledByDisplayMode } from '@/types/menuItem';
import { formatNextAvailableAt, DEFAULT_STORE_TIMEZONE } from '@/utils/menuAvailability';
import { menuItemAvailabilityTranslations } from '@/i18n/menuItemAvailability';

interface MenuItemCardProps {
  item: MenuItem;
  translations: {
    menuItems: Record<string, { name: string; description: string }>;
    discount: string;
    shortDays: Record<number, string>;
  };
  formatPrice: (price: number, withSymbol?: boolean) => string;
  onClick: () => void;
  /**
   * 매장 IANA 타임존 (#1128) — 다음 판매 시각 표기 기준.
   * 미전달 시 DEFAULT_STORE_TIMEZONE 폴백 (멕시코시티).
   */
  storeTimezone?: string;
}

export function MenuItemCard({
  item,
  translations,
  formatPrice,
  onClick,
  storeTimezone,
}: MenuItemCardProps) {
  const { language, locale } = useLanguage();
  // #menu-ui TASK-11: 비가용 상태 라벨/토스트를 15-locale 사전 경유(인라인 ko/es/en switch 대체).
  const availabilityT = menuItemAvailabilityTranslations[locale];
  const today = new Date().getDay();
  const isSoldOut = isMenuItemSoldOut(item);
  const isAvailableNow = isMenuItemAvailableNow(item, today);
  // Inactive(관리자 판매 OFF, 품절 아님) = BE MenuStatus::Inactive. #269 합의상 오버레이 없이 클릭만 차단.
  const isInactive = !item.isAvailable && !isSoldOut;
  // #1439 G4 — display_state='disabled' (영업시간 외 grayed out + 클릭 차단)
  const isDisabledByDisplayMode = isMenuItemDisabledByDisplayMode(item);
  const isClickable = !isSoldOut && !isInactive && isAvailableNow && !isDisabledByDisplayMode;
  // 오버레이 대상: 품절 · "시간 기반 비가용" · display_mode='disabled'. Inactive는 제외.
  const shouldShowOverlay = isSoldOut || (!isAvailableNow && !isInactive) || isDisabledByDisplayMode;

  /**
   * #279: 영업시간 외 메뉴는 다음 판매 시각을 함께 표기 (sold_out에는 미적용 — BE도 미반환).
   *       오버레이 좁은 영역에 들어가도록 줄바꿈으로 합성.
   */
  const getUnavailableText = () => {
    if (isSoldOut) {
      return availabilityT.soldOut;
    }
    const reason = item.availability?.reason;
    // #279 + #1128: 매장 타임존 기준 시각 표기.
    //   - PickupMenu가 storeInfo.timezone을 prop으로 전달 (BE PR #1129로 노출됨)
    //   - 미전달/null인 매장은 DEFAULT_STORE_TIMEZONE(멕시코시티) 폴백
    //     (BE도 timezone NOT NULL 제약이지만 정합성 결함 대비 이중 방어)
    const nextAvailable = formatNextAvailableAt(
      item.availability?.next_available_at,
      language,
      undefined,
      storeTimezone || DEFAULT_STORE_TIMEZONE,
    );
    const appendNext = (base: string): string =>
      nextAvailable ? `${base}\n${nextAvailable}` : base;

    if (reason === 'store_closed') {
      return appendNext(availabilityT.storeClosed);
    }
    if (reason === 'outside_menu_hours' || reason === 'outside_category_hours') {
      return appendNext(availabilityT.outsideHours);
    }
    // #1439 G4 — display_state='disabled' (BE 가 영업시간 외 grayed out 마크)
    if (isDisabledByDisplayMode) {
      return appendNext(availabilityT.outsideHours);
    }
    // fallback: mock availableDays 경로 또는 예상 외 reason. isInactive는 shouldShowOverlay로 이미 걸러짐.
    return availabilityT.notToday;
  };

  // API 데이터 우선 참조 (#63: 목업 데이터 대신 실제 API 데이터 사용)
  const itemName = item._name || translations.menuItems[item.nameKey as keyof typeof translations.menuItems]?.name || item.nameKey;
  const itemDescription = item._description || translations.menuItems[item.descriptionKey as keyof typeof translations.menuItems]?.description || '';

  // 요일 배지 렌더링 (mock `availableDays`가 있는 메뉴에만 해당)
  const renderDayBadges = () => {
    if (!item.availableDays) return null;
    const isAvailableToday = item.availableDays.includes(today);

    const shortDayNames = item.availableDays.map(day =>
      translations.shortDays[day as keyof typeof translations.shortDays]
    ).join(',');

    const showMultipleBadges = item.availableDays.length <= 3;

    return (
      <div className="absolute top-[clamp(4px,1vw,8px)] right-[clamp(4px,1vw,8px)] z-20 flex flex-nowrap gap-[clamp(2px,0.5vw,3px)] justify-end max-w-[clamp(120px,30vw,150px)]">
        {showMultipleBadges ? (
          item.availableDays.map((day) => (
            <Badge
              key={day}
              className={`text-[clamp(7.92px,1.98vw,11.88px)] px-[clamp(3px,0.8vw,5px)] py-[clamp(1px,0.3vw,2px)] font-medium leading-tight whitespace-nowrap ${
                day === today
                  ? 'bg-primary text-white shadow-sm'
                  : isAvailableToday
                  ? 'bg-primary/70 text-white shadow-sm'
                  : 'bg-border text-muted-foreground'
              }`}
            >
              {translations.shortDays[day as keyof typeof translations.shortDays]}
            </Badge>
          ))
        ) : (
          <Badge
            className={`text-[clamp(7.92px,1.98vw,11.88px)] px-[clamp(3px,0.8vw,5px)] py-[clamp(1px,0.3vw,2px)] font-medium leading-tight ${
              isAvailableToday
                ? 'bg-primary text-white shadow-sm'
                : 'bg-border text-muted-foreground'
            }`}
          >
            {shortDayNames}
          </Badge>
        )}
      </div>
    );
  };

  // 설명 텍스트 (#63: 하드코딩된 폴백 제거)
  const getDescriptionText = () => {
    return itemDescription || '';
  };

  // 세트메뉴 배지 텍스트
  const getSetMenuText = () => {
    const count = item.setMenuItemCount || 0;
    switch (language) {
      case 'ko':
        return count > 0 ? `세트 ${count}종` : '세트';
      case 'es':
        return count > 0 ? `Set ${count}` : 'Set';
      default:
        return count > 0 ? `Set ${count}` : 'Set';
    }
  };

  return (
    <Card
      className={`p-[clamp(8px,2vw,16px)] bg-background rounded-[5px] shadow-sm border border-border transition-all duration-200 -mt-[5px] ${
        !isClickable
          ? 'opacity-50 cursor-not-allowed grayscale'
          : 'hover:shadow-md cursor-pointer'
      }`}
      onClick={() => {
        if (isClickable) {
          onClick();
          return;
        }
        // #325: 영업시간 외/품절/비활성 메뉴 클릭 시 차단 사유 토스트 안내
        const reason = item.availability?.reason;
        const nextAvailable = formatNextAvailableAt(
          item.availability?.next_available_at,
          language,
          undefined,
          storeTimezone || DEFAULT_STORE_TIMEZONE,
        );
        let message: string;
        if (isSoldOut) {
          message = availabilityT.soldOutToast;
        } else if (reason === 'store_closed' || reason === 'outside_menu_hours' || reason === 'outside_category_hours') {
          message = nextAvailable
            ? availabilityT.availableAfter.replace('{nextAvailable}', nextAvailable)
            : availabilityT.notAvailableNow;
        } else {
          message = availabilityT.notAvailableGeneric;
        }
        toast.info(message);
      }}
    >
      <div className="content-stretch flex gap-[clamp(11px,2.75vw,16px)] items-center relative size-full font-['Noto_Sans',sans-serif]">
        {/* 메뉴 이미지 */}
        <div className="relative shrink-0 size-[clamp(70px,18vw,90px)]">
          <div className="absolute inset-0 pointer-events-none rounded-[5px]">
            <ImageWithFallback
              src={item.imageUrl}
              alt={itemName}
              className="absolute inset-0 max-w-none object-50%-50% object-cover rounded-[5px] size-full"
            />
            <div aria-hidden="true" className="absolute border-[0.25px] border-solid border-white inset-0 rounded-[5px]" />
          </div>

          {/* 품절/오늘 불가 오버레이 — Inactive는 클릭 차단만, 오버레이는 표시하지 않음 */}
          {shouldShowOverlay && (
            <>
              <div className="absolute left-0 size-[clamp(70px,18vw,90px)] top-0">
                <div className="absolute bg-black/50 inset-0 rounded-[5px]" />
              </div>
              <div className="absolute left-0 size-[clamp(70px,18vw,90px)] top-0 flex items-center justify-center pointer-events-none">
                <span
                  className="text-white font-bold text-[clamp(10px,2.5vw,14px)] text-center px-2 leading-tight whitespace-pre-line"
                  aria-label={getUnavailableText().replace(/\n/g, ' — ')}
                >
                  {getUnavailableText()}
                </span>
              </div>
            </>
          )}

          {/*
            할인 배지 — 타입과 값이 유효한 경우에만 수치 배지 노출.
            - percentage: value > 0이면 `-N%`
            - fixed_amount: amount > 0이면 `-금액`
            - 그 외(알 수 없는 type, 0/음수, discount 메타 부재)는 문자열 폴백
          */}
          {hasDiscount(item) && (
            <Badge className="absolute -top-[clamp(4px,1vw,8px)] -right-[clamp(4px,1vw,8px)] bg-warning text-white text-[clamp(8px,2vw,12px)] px-[clamp(4px,1vw,8px)] py-[clamp(2px,0.5vw,4px)] shadow-sm z-10">
              {item.discount?.type === 'percentage' && item.discount.value > 0
                ? `-${Math.round(item.discount.value)}%`
                : item.discount?.type === 'fixed_amount' && item.discount.amount > 0
                  ? `-${formatPrice(item.discount.amount, true)}`
                  : translations.discount}
            </Badge>
          )}

          {/* 세트메뉴 배지 */}
          {item.isSetMenu && (
            <Badge className="absolute -bottom-[clamp(4px,1vw,8px)] -left-[clamp(4px,1vw,8px)] bg-badge-indigo text-white text-[clamp(8px,2vw,12px)] px-[clamp(4px,1vw,8px)] py-[clamp(2px,0.5vw,4px)] shadow-sm z-10 font-medium">
              {getSetMenuText()}
            </Badge>
          )}
        </div>

        {/* 메뉴 정보 */}
        <div className="flex flex-1 min-w-0 flex-col justify-between h-[clamp(70px,18vw,90px)] items-start relative text-foreground py-[2px]">
          {/* 가격 섹션 */}
          <div className="flex flex-col font-['Noto_Sans',sans-serif] font-medium leading-[0] relative shrink-0 w-full">
            {item.originalPrice != null && item.originalPrice > 0 ? (
              <p className="leading-[normal] max-[370px]:scale-[0.8] max-[370px]:origin-left">
                <span className="[text-decoration-skip-ink:none] [text-underline-position:from-font] decoration-solid font-['Noto_Sans',sans-serif] font-medium line-through text-muted-foreground text-[clamp(11px,2.8vw,14px)] max-[339px]:text-[clamp(8.8px,2.24vw,11.2px)] mr-1">
                  {formatPrice(item.originalPrice, true)}
                </span>
                <span className="max-[339px]:text-[clamp(12.8px,4.4vw,17.6px)] max-[340px]:text-[12px] min-[400px]:text-[18px] text-foreground font-medium text-[14px]">
                  {formatPrice(item.price, true)}
                </span>
              </p>
            ) : (
              <p className="leading-[normal] text-[18px] max-[340px]:text-[15px] text-foreground self-start">
                {formatPrice(item.price, true)}
              </p>
            )}
          </div>

          {/* 메뉴 이름 */}
          <p className="font-['Noto_Sans',sans-serif] font-bold leading-[1.2] relative text-[clamp(13px,3.9vw,16px)] w-full min-w-0 line-clamp-1">
            {itemName}
          </p>

          {/* 설명 */}
          <p
            className="font-['Noto_Sans',sans-serif] font-normal text-[clamp(9px,2.4vw,12px)] leading-[1.2] w-full pr-[clamp(30px,7vw,40px)] line-clamp-2 overflow-hidden"
          >
            {getDescriptionText()}
          </p>
        </div>

        {/* 요일 배지 */}
        {renderDayBadges()}
      </div>
    </Card>
  );
}
